import { useCallback, useEffect, useState } from 'react';
import Storage from 'react-native-storage';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { createGlobalState } from 'react-native-global-state-hooks';

const storage = new Storage({
    size: 1000,
    storageBackend: AsyncStorage,
});

export type UsePersistedKeyValueStoreArgs<TDataType> = {
    category: string;
    key: string;
    defaultValue?: TDataType;
    expiration?: number;
    onPersisted?: (value: TDataType) => void;
    areValuesEqual?: (newValue: TDataType, existing: TDataType | undefined) => boolean;
};

export type UsePersistedKeyValueStoreReturnValue<TDataType> = {
    value: TDataType;
    isLoading: boolean;
    setValue: (value: TDataType) => Promise<void>;
};

export const getStorageKey = (category: string, key: string) => `${category}:${key}`;

export async function loadPersistedKeyValueStore<TDataType>(category: string, key: string, defaultValue?: TDataType) {
    try {
        const loadedValue = await storage.load({
            key: getStorageKey(category, key),
        });
        return loadedValue;
    } catch (e) {
        return defaultValue;
    }
}

const useGlobalState = createGlobalState<Record<string, any>>({});

export default function usePersistedKeyValueStore<TDataType>({
    category,
    key,
    defaultValue,
    expiration,
    onPersisted,
    areValuesEqual,
}: UsePersistedKeyValueStoreArgs<TDataType>): UsePersistedKeyValueStoreReturnValue<TDataType> {
    const [globalState, setGlobalState] = useGlobalState();
    const storageKey = getStorageKey(category, key);
    const [isLoading, setIsLoading] = useState(globalState[storageKey] === undefined);

    useEffect(() => {
        const loadValue = async () => {
            try {
                const loadedValue = await loadPersistedKeyValueStore(category, key, defaultValue);
                setGlobalState((_) => {
                    const isStillEmpty = _[storageKey] === undefined;

                    if (!isStillEmpty) return _;

                    return {
                        ..._,
                        [storageKey]: loadedValue,
                    };
                });
            } catch (e) {
                setGlobalState((_) => ({
                    ..._,
                    [storageKey]: defaultValue,
                }));
            } finally {
                setIsLoading(false);
            }
        };

        // only load if not yet in global state
        if (globalState[storageKey] === undefined) {
            loadValue();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [category, key, defaultValue]);

    const setValueAndPersist = useCallback(
        async (value: TDataType) => {
            setGlobalState((globalState) => {
                const isEqual = areValuesEqual
                    ? areValuesEqual(value, globalState[storageKey])
                    : value === globalState[storageKey];

                if (!isEqual) {
                    return {
                        ...globalState,
                        [storageKey]: value,
                    };
                } else {
                    return globalState;
                }
            });

            await storage.save({
                key: storageKey,
                data: value,
                expires: expiration,
            });
            onPersisted?.(value);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [expiration, storageKey, onPersisted],
    );

    return {
        value: globalState[storageKey] ?? defaultValue,
        isLoading,
        setValue: setValueAndPersist,
    };
}
